import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  MoreHorizontal,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Receipt,
  ShoppingCart,
  FileText,
  AlertTriangle,
  Search,
  X,
  Package,
  Loader2,
  Flag,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  UserPlus,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/lib/api';
import { categoriasConfig, compraItems, type CategoriaFactura, type SubtipoFactura, type CompraItem } from './TipoFacturaSelector';
import { useCodigosContables } from '@/hooks/useCodigosContables';

// Helpers para extraer items del XML de factura
function xmlGetText(xml: string, tag: string): string | undefined {
  for (const t of tag.split('|')) {
    const m = xml.match(new RegExp(`<(?:[a-z]+:)?${t}[^>]*>([^<]*)<\\/(?:[a-z]+:)?${t}>`, 'i'));
    if (m?.[1]) return m[1].trim();
  }
  return undefined;
}

function xmlGetAttr(xml: string, tag: string, attr: string): string | undefined {
  for (const t of tag.split('|')) {
    const m = xml.match(new RegExp(`<(?:[a-z]+:)?${t}[^>]*${attr}="([^"]*)"`, 'i'));
    if (m?.[1]) return m[1].trim();
  }
  return undefined;
}

function xmlExtractAll(xml: string, tag: string): string[] {
  const sections: string[] = [];
  const patterns = [
    new RegExp(`<(?:[a-z]+:)?${tag}[^>]*>[\\s\\S]*?<\\/(?:[a-z]+:)?${tag}>`, 'gi'),
    new RegExp(`<${tag}[^>]*>[\\s\\S]*?<\\/${tag}>`, 'gi'),
  ];
  for (const r of patterns) {
    const matches = xml.match(r);
    if (matches && matches.length > 0) { sections.push(...matches); break; }
  }
  return sections;
}

function xmlSection(xml: string, tag: string): string | undefined {
  const patterns = [
    new RegExp(`<(?:[a-z]+:)?${tag}[^>]*>[\\s\\S]*?<\\/(?:[a-z]+:)?${tag}>`, 'i'),
    new RegExp(`<${tag}[^>]*>[\\s\\S]*?<\\/${tag}>`, 'i'),
  ];
  for (const r of patterns) {
    const m = xml.match(r);
    if (m?.[0]) return m[0];
  }
  return undefined;
}

function pNum(v: string | undefined): number {
  if (!v) return 0;
  const n = parseFloat(v.replace(/[^\d.-]/g, ''));
  return isNaN(n) ? 0 : n;
}

interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  price: number;
  subtotal: number;
  tax: number;
  externalCode: string;
  internalCode: string | null;
  taxPercent: number;
  taxSchemeId: string;
}

function extractInvoiceLinesFromXml(xmlOriginal: string): InvoiceLineItem[] {
  const cdata = xmlOriginal.match(/<!\[CDATA\[([\s\S]*?)\]\]>/);
  const xml = cdata?.[1] || xmlOriginal;
  const lines: InvoiceLineItem[] = [];

  // Soportar InvoiceLine (facturas) y CreditNoteLine (notas credito)
  let lineSections = xmlExtractAll(xml, 'InvoiceLine');
  if (lineSections.length === 0) lineSections = xmlExtractAll(xml, 'CreditNoteLine');

  for (const lineXml of lineSections) {
    const id = xmlGetText(lineXml, 'ID') || '';
    const qtyMatch = lineXml.match(/<(?:[a-z]+:)?(?:Invoiced|Credited)Quantity[^>]*unitCode="([^"]*)"[^>]*>([^<]*)<\/(?:[a-z]+:)?(?:Invoiced|Credited)Quantity>/i);
    const quantity = pNum(qtyMatch?.[2] || xmlGetText(lineXml, 'InvoicedQuantity') || xmlGetText(lineXml, 'CreditedQuantity'));
    const unit = qtyMatch?.[1] || xmlGetAttr(lineXml, 'InvoicedQuantity', 'unitCode') || xmlGetAttr(lineXml, 'CreditedQuantity', 'unitCode') || 'UN';
    const subtotal = pNum(xmlGetText(lineXml, 'LineExtensionAmount'));
    const itemSec = xmlSection(lineXml, 'Item');
    const description = xmlGetText(itemSec || lineXml, 'Description') || xmlGetText(itemSec || lineXml, 'Name') || 'Sin descripcion';
    const sellersIdSec = xmlSection(itemSec || '', 'SellersItemIdentification');
    const sellersItemId = xmlGetText(sellersIdSec || '', 'ID');
    const externalCode = sellersItemId || description;
    const priceSec = xmlSection(lineXml, 'Price');
    const price = pNum(xmlGetText(priceSec || '', 'PriceAmount'));
    // Extract ALL TaxTotal sections per line (can have multiple: IVA, INC, ICUI, etc.)
    const taxSections = xmlExtractAll(lineXml, 'TaxTotal');
    let tax = 0;
    for (const ts of taxSections) {
      tax += pNum(xmlGetText(ts, 'TaxAmount'));
    }

    // Extract per-line tax percent and scheme - prefer IVA (01) > INC (04) > first found
    let taxPercent = 0;
    let taxSchemeId = '';
    let fallbackPercent = 0;
    let fallbackSchemeId = '';
    for (const ts of taxSections) {
      const subSec = xmlSection(ts, 'TaxSubtotal');
      if (subSec) {
        const catSec = xmlSection(subSec, 'TaxCategory');
        const pct = pNum(xmlGetText(catSec || '', 'Percent'));
        const schemeSec = xmlSection(subSec, 'TaxScheme');
        const schId = xmlGetText(schemeSec || '', 'ID') || '';
        if (schId === '01') { // IVA has highest priority
          taxPercent = pct;
          taxSchemeId = schId;
          break;
        }
        if (schId === '04' && !fallbackSchemeId) { // INC is second
          fallbackPercent = pct;
          fallbackSchemeId = schId;
        }
        if (!fallbackSchemeId) { // First found as last resort
          fallbackPercent = pct;
          fallbackSchemeId = schId;
        }
      }
    }
    if (!taxSchemeId && fallbackSchemeId) {
      taxPercent = fallbackPercent;
      taxSchemeId = fallbackSchemeId;
    }

    lines.push({ id, description, quantity, unit, price, subtotal, tax, externalCode, internalCode: null, taxPercent, taxSchemeId });
  }
  return lines;
}

interface Factura {
  id: string;
  numero: string;
  proveedor?: { razon_social: string; nit?: string };
  fecha_emision: string;
  fecha_vencimiento?: string;
  valor_total: number;
  servicio?: { nombre: string; codigo: string };
  estado: string;
  prioridad: string;
  usuario_asignado?: { nombre: string };
  receptor_nit?: string;
  receptor_nombre?: string;
  tipo_factura?: string;
  subtipo_factura?: string;
  tipo_iva?: string;
  tipo_documento?: string;
  tax_percent?: string | null;
  lineas_iva?: string | null;
  lineas_subtipo?: string | null;
  erp?: boolean;
}

type TipoFactura = 'gasto' | 'compra' | 'nota_credito';

const tipoFacturaConfig: Record<TipoFactura, { label: string; color: string; icon: React.ReactNode }> = {
  gasto: {
    label: 'Gasto',
    color: 'bg-orange-100 text-orange-800',
    icon: <Receipt className="h-3 w-3" />,
  },
  compra: {
    label: 'Compra',
    color: 'bg-blue-100 text-blue-800',
    icon: <ShoppingCart className="h-3 w-3" />,
  },
  nota_credito: {
    label: 'N. Crédito',
    color: 'bg-green-100 text-green-800',
    icon: <FileText className="h-3 w-3" />,
  },
};

// Helper para obtener el nombre del subtipo.
// `codigos` es la lista viva de códigos contables (DB + fallback al array); se pasa
// como parámetro porque esta función es module-scope y el catálogo ahora es dinámico.
function getSubtipoNombre(categoria: string, subtipoId: string, codigos: CompraItem[] = compraItems): string {
  // Para gasto, buscar en el catálogo de códigos contables
  if (categoria === 'gasto') {
    const compraItem = codigos.find(item => item.codigo === subtipoId);
    if (compraItem) return compraItem.nombre;
  }
  // Para compra, el subtipo es siempre 'items_confirmados'
  if (categoria === 'compra' && subtipoId === 'items_confirmados') {
    return 'Items confirmados';
  }
  // Para nota_credito, puede ser código contable o mercancía confirmada
  if (categoria === 'nota_credito') {
    if (subtipoId === 'mercancia_confirmada') return 'Mercancía';
    const compraItem = codigos.find(item => item.codigo === subtipoId);
    if (compraItem) return compraItem.nombre;
  }
  // Para otros tipos, buscar en subtipos
  const cat = categoriasConfig[categoria as CategoriaFactura];
  if (!cat) return subtipoId;
  const subtipo = cat.subtipos.find(s => s.id === subtipoId);
  return subtipo?.nombre || subtipoId;
}

// Configuración de tipos de IVA
const tiposIvaConfig: { value: string; label: string; short: string }[] = [
  { value: '0', label: 'VARIOS', short: 'Varios' },
  { value: 'TAR0', label: 'VALOR VENTAS EXCLUIDAS', short: 'Excluido' },
  { value: 'TAR1', label: 'TARIFA 1 (19%)', short: '19%' },
  { value: 'TAR2', label: 'TARIFA 2 (5%)', short: '5%' },
  { value: 'TAR3', label: 'TARIFA 3 (35%)', short: '35%' },
  { value: 'TAR4', label: 'TARIFA 4 (45%)', short: '45%' },
  { value: 'TAR5', label: 'TARIFA 5 (60%)', short: '60%' },
  { value: 'TAR6', label: 'TARIFA 6 (OTRAS)', short: 'Otras' },
  { value: 'TAR7', label: 'IVA 1%', short: '1%' },
  { value: 'TAR8', label: 'IVA 19% IMPORTACIONES', short: '19% Imp.' },
  { value: 'TAR9', label: 'IVA 19% SERVICIOS', short: '19% Serv.' },
  { value: 's51152001', label: 'Imp Asumido 1', short: 'INC 1' },
  { value: 's52152001', label: 'Imp Asumido 2', short: 'INC 2' },
];

function getTipoIvaLabel(value: string): string {
  const tipo = tiposIvaConfig.find(t => t.value === value);
  return tipo?.short || value;
}

function suggestTipoIva(taxPercent: number, taxSchemeId?: string): string {
  // INC scheme (04) → suggest Imp Asumido 1
  if (taxSchemeId === '04') {
    return 's51152001';
  }
  // Non-IVA schemes (ICUI, etc.) → OTRAS or EXCLUIDAS
  if (taxSchemeId && taxSchemeId !== '01') {
    if (taxPercent === 0) return 'TAR0'; // EXCLUIDAS (e.g., ICUI at 0%)
    return 'TAR6'; // OTRAS
  }
  // Standard IVA scheme
  if (taxPercent === 0) return 'TAR0';
  if (taxPercent === 19) return 'TAR1';
  if (taxPercent === 5) return 'TAR2';
  if (taxPercent === 35) return 'TAR3';
  if (taxPercent === 45) return 'TAR4';
  if (taxPercent === 60) return 'TAR5';
  if (taxPercent === 1) return 'TAR7';
  return 'TAR6';
}

// Configuración de tipos de documento DIAN
const tipoDocumentoConfig: Record<string, { label: string; color: string }> = {
  '01': { label: 'Factura de Venta', color: 'bg-indigo-100 text-indigo-800' },
  '91': { label: 'Nota Crédito', color: 'bg-teal-100 text-teal-800' },
  '92': { label: 'Nota Débito', color: 'bg-rose-100 text-rose-800' },
};

function getTipoDocumentoLabel(value: string): { label: string; color: string } {
  return tipoDocumentoConfig[value] || { label: value, color: 'bg-gray-100 text-gray-800' };
}

interface FacturasTableProps {
  facturas: Factura[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  total?: number;
  onPageChange: (page: number) => void;
  limit?: number;
  onRefresh?: () => void;
  userRole?: string;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (sortBy: string, sortDir: 'asc' | 'desc') => void;
}

type ActionType = 'aprobar' | 'en_espera' | 'rechazar' | 'eliminar';
type BulkActionType = 'aprobar' | 'en_espera' | 'rechazar' | 'eliminar';

interface ConfirmDialogState {
  open: boolean;
  action: ActionType | null;
  factura: Factura | null;
}

interface BulkConfirmDialogState {
  open: boolean;
  action: BulkActionType | null;
}

const estadoVariants: Record<string, 'default' | 'success' | 'destructive' | 'warning' | 'info'> = {
  pendiente: 'warning',
  aprobada: 'success',
  rechazada: 'destructive',
  en_espera: 'info',
};

const estadoLabels: Record<string, string> = {
  pendiente: 'Pendiente',
  aprobada: 'Aprobada',
  rechazada: 'Rechazada',
  en_espera: 'En Espera',
};

const actionConfig: Record<ActionType, {
  title: string;
  description: string;
  confirmText: string;
  variant: 'default' | 'destructive';
  icon: React.ReactNode;
}> = {
  aprobar: {
    title: 'Aprobar Factura',
    description: 'Esta seguro que desea aprobar esta factura? Esta accion cambiara el estado a "Aprobada".',
    confirmText: 'Aprobar',
    variant: 'default',
    icon: <CheckCircle className="h-4 w-4 text-green-600" />,
  },
  en_espera: {
    title: 'Poner en Espera',
    description: 'Esta seguro que desea poner esta factura en espera? Podra cambiar el estado posteriormente.',
    confirmText: 'Poner en Espera',
    variant: 'default',
    icon: <Clock className="h-4 w-4 text-blue-600" />,
  },
  rechazar: {
    title: 'Rechazar Factura',
    description: 'Esta seguro que desea rechazar esta factura? Esta accion cambiara el estado a "Rechazada".',
    confirmText: 'Rechazar',
    variant: 'destructive',
    icon: <XCircle className="h-4 w-4 text-red-600" />,
  },
  eliminar: {
    title: 'Eliminar Factura',
    description: 'Esta seguro que desea eliminar esta factura? Esta accion no se puede deshacer.',
    confirmText: 'Eliminar',
    variant: 'destructive',
    icon: <Trash2 className="h-4 w-4 text-red-600" />,
  },
};

const bulkActionConfig: Record<BulkActionType, {
  title: string;
  description: (count: number) => string;
  confirmText: string;
  variant: 'default' | 'destructive';
  icon: React.ReactNode;
}> = {
  aprobar: {
    title: 'Aprobar Facturas',
    description: (count) => `Esta seguro que desea aprobar ${count} facturas? Esta accion cambiara su estado a "Aprobada".`,
    confirmText: 'Aprobar Todas',
    variant: 'default',
    icon: <CheckCircle className="h-4 w-4 text-green-600" />,
  },
  en_espera: {
    title: 'Poner en Espera',
    description: (count) => `Esta seguro que desea poner ${count} facturas en espera?`,
    confirmText: 'Poner en Espera',
    variant: 'default',
    icon: <Clock className="h-4 w-4 text-blue-600" />,
  },
  rechazar: {
    title: 'Rechazar Facturas',
    description: (count) => `Esta seguro que desea rechazar ${count} facturas? Esta accion cambiara su estado a "Rechazada".`,
    confirmText: 'Rechazar Todas',
    variant: 'destructive',
    icon: <XCircle className="h-4 w-4 text-red-600" />,
  },
  eliminar: {
    title: 'Eliminar Facturas',
    description: (count) => `Esta seguro que desea eliminar ${count} facturas? Esta accion no se puede deshacer.`,
    confirmText: 'Eliminar Todas',
    variant: 'destructive',
    icon: <Trash2 className="h-4 w-4 text-red-600" />,
  },
};

export function FacturasTable({
  facturas,
  isLoading,
  page,
  totalPages,
  total,
  onPageChange,
  limit = 20,
  onRefresh,
  userRole,
  onSelectionChange,
  sortBy,
  sortDir,
  onSortChange,
}: FacturasTableProps) {
  const isAdmin = userRole === 'admin';
  const navigate = useNavigate();
  // Catálogo de códigos contables (DB, con fallback al array hardcodeado).
  const { todos: codigosContables, activos: codigosContablesActivos } = useCodigosContables();
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    action: null,
    factura: null,
  });
  const [bulkConfirmDialog, setBulkConfirmDialog] = useState<BulkConfirmDialogState>({
    open: false,
    action: null,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    onSelectionChange?.(selectedIds);
  }, [selectedIds, onSelectionChange]);

  // Estado para el modal de subtipos
  const [subtipoModal, setSubtipoModal] = useState<{
    open: boolean;
    categoria: CategoriaFactura | null;
    factura: Factura | null;
  }>({
    open: false,
    categoria: null,
    factura: null,
  });

  // Estado para mostrar alerta de validación
  const [validationAlert, setValidationAlert] = useState<{
    open: boolean;
    factura: Factura | null;
  }>({ open: false, factura: null });

  // Estado para alerta de validación bulk
  const [bulkValidationAlert, setBulkValidationAlert] = useState<{
    open: boolean;
    invalidFacturas: Factura[];
    validFacturas: Factura[];
  }>({ open: false, invalidFacturas: [], validFacturas: [] });

  // Estado para registro de proveedor (cuando falta en catálogo)
  const [supplierRegisterDialog, setSupplierRegisterDialog] = useState<{
    open: boolean;
    factura: Factura | null;
    proveedor_nit: string;
    proveedor_razon_social: string;
    existingSupplier: any | null;
    formData: {
      codigo_cta: string;
      cuenta_x_pagar: string;
      cod_retencion: string;
      rtefte: string;
      cod_rete: string;
      top_rete: string;
      po_rete: string;
      cod_cc: string;
    };
    loading: boolean;
  }>({
    open: false,
    factura: null,
    proveedor_nit: '',
    proveedor_razon_social: '',
    existingSupplier: null,
    formData: {
      codigo_cta: '',
      cuenta_x_pagar: '',
      cod_retencion: '',
      rtefte: '',
      cod_rete: '',
      top_rete: '',
      po_rete: '',
      cod_cc: '',
    },
    loading: false,
  });

  // Estado para loading de verificación de proveedor
  const [checkingSupplier, setCheckingSupplier] = useState(false);

  // Estado para busqueda en modal de compra
  const [compraSearch, setCompraSearch] = useState('');

  const openConfirmDialog = async (action: ActionType, factura: Factura) => {
    // Validar que tenga tipo_factura y tipo_iva para aprobar
    if (action === 'aprobar' && (!factura.tipo_factura || !factura.tipo_iva)) {
      setValidationAlert({ open: true, factura });
      return;
    }

    // Validar proveedor en catálogo para aprobar
    if (action === 'aprobar') {
      setCheckingSupplier(true);
      try {
        const result = await api.checkFacturaSupplier(factura.id);
        if (!result.found) {
          // Proveedor no encontrado o sin CTOCTA - abrir dialog de registro
          const existing = result.supplier || null;
          setSupplierRegisterDialog({
            open: true,
            factura,
            proveedor_nit: result.proveedor_nit || '',
            proveedor_razon_social: result.proveedor_razon_social || '',
            existingSupplier: existing,
            formData: {
              codigo_cta: existing?.codigo_cta || '',
              cuenta_x_pagar: existing?.cuenta_x_pagar || '',
              cod_retencion: existing?.cod_retencion || '',
              rtefte: existing?.rtefte?.toString() || '',
              cod_rete: existing?.cod_rete || '',
              top_rete: existing?.top_rete?.toString() || '',
              po_rete: existing?.po_rete?.toString() || '',
              cod_cc: existing?.cod_cc || '',
            },
            loading: false,
          });
          return;
        }
      } catch (error) {
        console.error('Error al verificar proveedor:', error);
      } finally {
        setCheckingSupplier(false);
      }
    }

    setConfirmDialog({ open: true, action, factura });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: null, factura: null });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog.action || !confirmDialog.factura) return;

    setIsProcessing(true);
    try {
      const { action, factura } = confirmDialog;

      if (action === 'eliminar') {
        await api.deleteFactura(factura.id);
      } else {
        const estadoMap: Record<string, string> = {
          aprobar: 'aprobada',
          en_espera: 'en_espera',
          rechazar: 'rechazada',
        };
        await api.updateEstado(factura.id, estadoMap[action]);
      }

      closeConfirmDialog();
      onRefresh?.();
    } catch (error) {
      console.error('Error al procesar accion:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRegisterAndApprove = async () => {
    const { factura, proveedor_nit, proveedor_razon_social, formData } = supplierRegisterDialog;
    if (!factura || !formData.codigo_cta) return;

    setSupplierRegisterDialog(prev => ({ ...prev, loading: true }));
    try {
      const cleanNit = proveedor_nit.split('-')[0].replace(/[.\s]/g, '');
      await api.registerSupplier({
        code: cleanNit,
        name: proveedor_razon_social || cleanNit,
        codigo_cta: formData.codigo_cta,
        cuenta_x_pagar: formData.cuenta_x_pagar || undefined,
        cod_retencion: formData.cod_retencion || undefined,
        rtefte: formData.rtefte ? parseFloat(formData.rtefte) : undefined,
        cod_rete: formData.cod_rete || undefined,
        top_rete: formData.top_rete ? parseInt(formData.top_rete) : undefined,
        po_rete: formData.po_rete ? parseFloat(formData.po_rete) : undefined,
        cod_cc: formData.cod_cc || undefined,
      });

      // Ahora aprobar la factura
      await api.updateEstado(factura.id, 'aprobada');
      setSupplierRegisterDialog(prev => ({ ...prev, open: false }));
      onRefresh?.();
    } catch (error) {
      console.error('Error al registrar proveedor:', error);
    } finally {
      setSupplierRegisterDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const openSubtipoModal = (categoria: CategoriaFactura, factura: Factura) => {
    if (categoria === 'compra') {
      openCompraModal(factura);
      return;
    }
    if (categoria === 'nota_credito') {
      openNotaCreditoModal(factura);
      return;
    }
    if (categoria === 'gasto') {
      openGastoLineasModal(factura);
      return;
    }
    setSubtipoModal({ open: true, categoria, factura });
  };

  const closeSubtipoModal = () => {
    setSubtipoModal({ open: false, categoria: null, factura: null });
    setCompraSearch('');
  };

  const handleSubtipoSelect = async (subtipo: SubtipoFactura) => {
    if (!subtipoModal.categoria || !subtipoModal.factura) return;

    setIsProcessing(true);
    try {
      await api.updateTipoFactura(
        subtipoModal.factura.id,
        subtipoModal.categoria,
        subtipo.id
      );
      closeSubtipoModal();
      onRefresh?.();
    } catch (error) {
      console.error('Error al actualizar tipo:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCompraSelect = async (item: CompraItem) => {
    if (!subtipoModal.factura) return;

    setIsProcessing(true);
    try {
      await api.updateTipoFactura(
        subtipoModal.factura.id,
        'gasto',
        item.codigo
      );
      closeSubtipoModal();
      onRefresh?.();
    } catch (error) {
      console.error('Error al actualizar tipo:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Filtrar items de compra segun busqueda
  const filteredCompraItems = codigosContablesActivos.filter((item) => {
    const searchLower = compraSearch.toLowerCase();
    return (
      item.codigo.toLowerCase().includes(searchLower) ||
      item.nombre.toLowerCase().includes(searchLower)
    );
  });

  // Estado para modal de Compra (items del XML)
  const [compraModal, setCompraModal] = useState<{
    open: boolean;
    factura: Factura | null;
    items: InvoiceLineItem[];
    loading: boolean;
    lookingUp: boolean;
    proveedorNit: string;
    newEquivalencias: Record<string, string>;
  }>({ open: false, factura: null, items: [], loading: false, lookingUp: false, proveedorNit: '', newEquivalencias: {} });

  // Estado para modal de Nota Crédito (2 categorías: gastos o mercancía)
  const [notaCreditoModal, setNotaCreditoModal] = useState<{
    open: boolean;
    factura: Factura | null;
    items: InvoiceLineItem[];
    loading: boolean;
    step: 'select' | 'gastos' | 'mercancia';
  }>({ open: false, factura: null, items: [], loading: false, step: 'select' });

  // Búsqueda para nota crédito de gastos
  const [ncGastoSearch, setNcGastoSearch] = useState('');

  // Retention option type
  type RetentionOption = {
    cod_retencion: string;
    rtefte: number | null;
    cod_rete: string | null;
    top_rete: number | null;
    po_rete: number | null;
  };

  // Estado para modal de IVA por líneas
  const [ivaModal, setIvaModal] = useState<{
    open: boolean;
    factura: Factura | null;
    lines: InvoiceLineItem[];
    lineAssignments: Record<string, string>;
    retencionAssignments: Record<string, string>; // line_id -> cod_retencion value (or '__none__')
    retentionOptions: RetentionOption[];
    defaultCodRetencion: string;
    loading: boolean;
  }>({ open: false, factura: null, lines: [], lineAssignments: {}, retencionAssignments: {}, retentionOptions: [], defaultCodRetencion: '', loading: false });

  const openIvaModal = async (factura: Factura) => {
    setIvaModal({ open: true, factura, lines: [], lineAssignments: {}, retencionAssignments: {}, retentionOptions: [], defaultCodRetencion: '', loading: true });
    try {
      const [data, retOptions] = await Promise.all([
        api.getFactura(factura.id),
        api.getRetentionOptions().catch(() => ({ data: [] })),
      ]);
      const xmlOriginal = data.xml_original || '';
      const lines = extractInvoiceLinesFromXml(xmlOriginal);

      // Parse existing lineas_iva if available
      let existingAssignments: Record<string, string> = {};
      if (data.lineas_iva) {
        try {
          const parsed = JSON.parse(data.lineas_iva);
          for (const la of parsed) {
            existingAssignments[la.line_id] = la.tipo_iva;
          }
        } catch { /* ignore parse errors */ }
      }

      // Parse existing lineas_retencion if available (stores cod_retencion key)
      let existingRetencion: Record<string, string> = {};
      if (data.lineas_retencion) {
        try {
          const parsed = JSON.parse(data.lineas_retencion);
          for (const lr of parsed) {
            existingRetencion[lr.line_id] = lr.cod_retencion || '__none__';
          }
        } catch { /* ignore parse errors */ }
      }

      // Fetch supplier default cod_retencion by NIT
      let defaultCodRetencion = '';
      const proveedorNit = data.proveedor_nit || factura.proveedor?.nit || '';
      if (proveedorNit) {
        try {
          const cleanNit = proveedorNit.split('-')[0].replace(/[.\s]/g, '');
          const suppliers = await api.searchSuppliersByNit(cleanNit);
          if (suppliers.data.length > 0) {
            defaultCodRetencion = suppliers.data[0].cod_retencion || '';
          }
        } catch { /* ignore lookup errors */ }
      }

      // Auto-populate: use existing assignments, or suggest from detected tax %
      const autoAssignments: Record<string, string> = {};
      const autoRetencion: Record<string, string> = {};
      for (const line of lines) {
        autoAssignments[line.id] = existingAssignments[line.id] || suggestTipoIva(line.taxPercent, line.taxSchemeId);
        autoRetencion[line.id] = existingRetencion[line.id] || defaultCodRetencion || '__none__';
      }

      setIvaModal(prev => ({ ...prev, lines, lineAssignments: autoAssignments, retencionAssignments: autoRetencion, retentionOptions: retOptions.data, defaultCodRetencion, loading: false }));
    } catch (error) {
      console.error('Error al cargar líneas para IVA:', error);
      setIvaModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleIvaLineChange = (lineId: string, value: string) => {
    setIvaModal(prev => ({
      ...prev,
      lineAssignments: { ...prev.lineAssignments, [lineId]: value },
    }));
  };

  const handleRetencionLineChange = (lineId: string, value: string) => {
    setIvaModal(prev => ({
      ...prev,
      retencionAssignments: { ...prev.retencionAssignments, [lineId]: value },
    }));
  };

  const handleIvaApplyAll = (value: string) => {
    setIvaModal(prev => {
      const newAssignments: Record<string, string> = {};
      for (const line of prev.lines) {
        newAssignments[line.id] = value;
      }
      return { ...prev, lineAssignments: newAssignments };
    });
  };

  const handleSaveIva = async () => {
    if (!ivaModal.factura) return;
    setIsProcessing(true);
    try {
      const lineasIva = ivaModal.lines.map(line => ({
        line_id: line.id,
        description: line.description,
        subtotal: line.subtotal,
        tax_amount: line.tax,
        tax_percent: line.taxPercent,
        tax_scheme_id: line.taxSchemeId,
        tipo_iva: ivaModal.lineAssignments[line.id] || '',
      }));

      const lineasRetencion = ivaModal.lines.map(line => {
        const selectedKey = ivaModal.retencionAssignments[line.id];
        if (!selectedKey || selectedKey === '__none__') {
          return { line_id: line.id, cod_retencion: '', rtefte: null, cod_rete: null, top_rete: null, po_rete: null };
        }
        const option = ivaModal.retentionOptions.find(o => o.cod_retencion === selectedKey);
        return {
          line_id: line.id,
          cod_retencion: selectedKey,
          rtefte: option?.rtefte ?? null,
          cod_rete: option?.cod_rete ?? null,
          top_rete: option?.top_rete ?? null,
          po_rete: option?.po_rete ?? null,
        };
      });

      // Derive global tipo_iva
      const uniqueValues = new Set(lineasIva.map(l => l.tipo_iva).filter(Boolean));
      const globalTipoIva = uniqueValues.size === 1 ? [...uniqueValues][0] : '0'; // '0' = VARIOS

      await api.updateLineasIva(ivaModal.factura.id, lineasIva, globalTipoIva, lineasRetencion);
      setIvaModal({ open: false, factura: null, lines: [], lineAssignments: {}, retencionAssignments: {}, retentionOptions: [], defaultCodRetencion: '', loading: false });
      onRefresh?.();
    } catch (error) {
      console.error('Error al guardar IVA por líneas:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Estado para modal de Gasto por líneas (subtipo per line)
  const [gastoLineasModal, setGastoLineasModal] = useState<{
    open: boolean;
    factura: Factura | null;
    lines: InvoiceLineItem[];
    lineAssignments: Record<string, string>; // line_id -> subtipo_factura (codigo contable)
    loading: boolean;
    search: string;
  }>({ open: false, factura: null, lines: [], lineAssignments: {}, loading: false, search: '' });

  const openGastoLineasModal = async (factura: Factura) => {
    setGastoLineasModal({ open: true, factura, lines: [], lineAssignments: {}, loading: true, search: '' });
    try {
      const data = await api.getFactura(factura.id);
      const xmlOriginal = data.xml_original || '';
      const lines = extractInvoiceLinesFromXml(xmlOriginal);

      // Parse existing lineas_subtipo if available
      let existingAssignments: Record<string, string> = {};
      if (data.lineas_subtipo) {
        try {
          const parsed = JSON.parse(data.lineas_subtipo);
          for (const ls of parsed) {
            existingAssignments[ls.line_id] = ls.subtipo_factura;
          }
        } catch { /* ignore parse errors */ }
      }

      // Use existing or leave empty for manual selection
      const autoAssignments: Record<string, string> = {};
      for (const line of lines) {
        autoAssignments[line.id] = existingAssignments[line.id] || '';
      }

      setGastoLineasModal(prev => ({ ...prev, lines, lineAssignments: autoAssignments, loading: false }));
    } catch (error) {
      console.error('Error al cargar líneas para subtipo gasto:', error);
      setGastoLineasModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleGastoLineChange = (lineId: string, value: string) => {
    setGastoLineasModal(prev => ({
      ...prev,
      lineAssignments: { ...prev.lineAssignments, [lineId]: value },
    }));
  };

  const handleGastoApplyAll = (value: string) => {
    setGastoLineasModal(prev => {
      const newAssignments: Record<string, string> = {};
      for (const line of prev.lines) {
        newAssignments[line.id] = value;
      }
      return { ...prev, lineAssignments: newAssignments };
    });
  };

  const handleSaveGastoLineas = async () => {
    if (!gastoLineasModal.factura) return;
    setIsProcessing(true);
    try {
      const lineasSubtipo = gastoLineasModal.lines.map(line => ({
        line_id: line.id,
        description: line.description,
        subtipo_factura: gastoLineasModal.lineAssignments[line.id] || '',
      }));

      // Derive global subtipo: if all same → that value, if mixed → first found
      const uniqueValues = new Set(lineasSubtipo.map(l => l.subtipo_factura).filter(Boolean));
      const globalSubtipo = uniqueValues.size === 1 ? [...uniqueValues][0] : lineasSubtipo.find(l => l.subtipo_factura)?.subtipo_factura || '';

      await api.updateLineasSubtipo(gastoLineasModal.factura.id, lineasSubtipo, 'gasto', globalSubtipo);
      setGastoLineasModal({ open: false, factura: null, lines: [], lineAssignments: {}, loading: false, search: '' });
      onRefresh?.();
    } catch (error) {
      console.error('Error al guardar subtipo por líneas:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Filtered compra items for gasto lineas modal
  const filteredGastoLineasItems = codigosContablesActivos.filter((item) => {
    const searchLower = gastoLineasModal.search.toLowerCase();
    if (!searchLower) return true;
    return item.codigo.toLowerCase().includes(searchLower) || item.nombre.toLowerCase().includes(searchLower);
  });

  const openCompraModal = async (factura: Factura) => {
    setCompraModal({ open: true, factura, items: [], loading: true, lookingUp: false, proveedorNit: '', newEquivalencias: {} });
    try {
      const data = await api.getFactura(factura.id);
      const xmlOriginal = data.xml_original || '';
      const items = extractInvoiceLinesFromXml(xmlOriginal);
      const proveedorNit = (data.proveedor_nit || '').trim();

      setCompraModal((prev) => ({ ...prev, items, loading: false, lookingUp: true, proveedorNit }));

      // Bulk lookup de equivalencias
      if (proveedorNit && items.length > 0) {
        try {
          const externalCodes = items.map((item) => item.externalCode);
          const result = await api.lookupEquivalencias(proveedorNit, externalCodes);

          const lookupMap = new Map<string, string>();
          for (const entry of result.data) {
            if (entry.internal_code) {
              lookupMap.set(entry.external_code, entry.internal_code);
            }
          }

          const resolvedItems = items.map((item) => ({
            ...item,
            internalCode: lookupMap.get(item.externalCode) || null,
          }));

          setCompraModal((prev) => ({ ...prev, items: resolvedItems, lookingUp: false }));
        } catch (error) {
          console.error('Error al buscar equivalencias:', error);
          setCompraModal((prev) => ({ ...prev, lookingUp: false }));
        }
      } else {
        setCompraModal((prev) => ({ ...prev, lookingUp: false }));
      }
    } catch (error) {
      console.error('Error al cargar items:', error);
      setCompraModal((prev) => ({ ...prev, loading: false, lookingUp: false }));
    }
  };

  const handleInternalCodeChange = (externalCode: string, value: string) => {
    setCompraModal((prev) => ({
      ...prev,
      newEquivalencias: {
        ...prev.newEquivalencias,
        [externalCode]: value,
      },
    }));
  };

  const allItemsHaveInternalCode = compraModal.items.length > 0 && compraModal.items.every((item) => {
    if (item.internalCode) return true;
    return (compraModal.newEquivalencias[item.externalCode] || '').trim().length > 0;
  });

  const handleConfirmCompra = async () => {
    if (!compraModal.factura) return;
    setIsProcessing(true);
    try {
      // Paso 1: Guardar nuevas equivalencias
      const newItems = Object.entries(compraModal.newEquivalencias)
        .filter(([_, code]) => code.trim().length > 0)
        .map(([externalCode, internalCode]) => {
          const item = compraModal.items.find((i) => i.externalCode === externalCode);
          return {
            external_code: externalCode,
            internal_code: internalCode.trim(),
            description: item?.description || '',
          };
        });

      if (newItems.length > 0 && compraModal.proveedorNit) {
        await api.saveEquivalencias(compraModal.proveedorNit, newItems);
      }

      // Paso 2: Confirmar compra
      await api.updateTipoFactura(compraModal.factura.id, 'compra', 'items_confirmados');
      setCompraModal({ open: false, factura: null, items: [], loading: false, lookingUp: false, proveedorNit: '', newEquivalencias: {} });
      onRefresh?.();
    } catch (error) {
      console.error('Error al confirmar compra:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const openNotaCreditoModal = async (factura: Factura) => {
    setNotaCreditoModal({ open: true, factura, items: [], loading: true, step: 'select' });
    setNcGastoSearch('');
    try {
      const data = await api.getFactura(factura.id);
      const xmlOriginal = data.xml_original || '';
      const items = extractInvoiceLinesFromXml(xmlOriginal);
      setNotaCreditoModal((prev) => ({ ...prev, items, loading: false }));
    } catch (error) {
      console.error('Error al cargar items:', error);
      setNotaCreditoModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const closeNotaCreditoModal = () => {
    setNotaCreditoModal({ open: false, factura: null, items: [], loading: false, step: 'select' });
    setNcGastoSearch('');
  };

  const handleNcGastoSelect = async (item: CompraItem) => {
    if (!notaCreditoModal.factura) return;
    setIsProcessing(true);
    try {
      await api.updateTipoFactura(notaCreditoModal.factura.id, 'nota_credito', item.codigo);
      closeNotaCreditoModal();
      onRefresh?.();
    } catch (error) {
      console.error('Error al asignar nota crédito:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmNcMercancia = async () => {
    if (!notaCreditoModal.factura) return;
    setIsProcessing(true);
    try {
      await api.updateTipoFactura(notaCreditoModal.factura.id, 'nota_credito', 'mercancia_confirmada');
      closeNotaCreditoModal();
      onRefresh?.();
    } catch (error) {
      console.error('Error al confirmar nota crédito mercancía:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredNcGastoItems = codigosContablesActivos.filter((item) => {
    const s = ncGastoSearch.toLowerCase();
    return item.codigo.toLowerCase().includes(s) || item.nombre.toLowerCase().includes(s);
  });

  // Funciones de selección
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(facturas.map((f) => f.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const isAllSelected = facturas.length > 0 && selectedIds.size === facturas.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < facturas.length;

  // Calcular valor total de facturas seleccionadas
  const selectedTotal = useMemo(() => {
    return facturas
      .filter((f) => selectedIds.has(f.id))
      .reduce((sum, f) => sum + f.valor_total, 0);
  }, [facturas, selectedIds]);

  // Funciones de acciones bulk
  const openBulkConfirmDialog = async (action: BulkActionType) => {
    // Validar campos requeridos para aprobar
    if (action === 'aprobar') {
      const selectedFacturas = facturas.filter((f) => selectedIds.has(f.id));
      const invalidFacturas = selectedFacturas.filter((f) => !f.tipo_factura || !f.tipo_iva);
      const validFacturas = selectedFacturas.filter((f) => f.tipo_factura && f.tipo_iva);

      if (invalidFacturas.length > 0) {
        setBulkValidationAlert({ open: true, invalidFacturas, validFacturas });
        return;
      }

      // Verificar proveedores en catálogo
      setCheckingSupplier(true);
      try {
        const ids = selectedFacturas.map(f => f.id);
        const result = await api.checkFacturasSuppliers(ids);
        if (result.missing.length > 0) {
          const missingIds = new Set(result.missing.map(m => m.id));
          const invalidBySupplier = selectedFacturas.filter(f => missingIds.has(f.id));
          const validBySupplier = selectedFacturas.filter(f => !missingIds.has(f.id));
          // Add supplier info to invalid facturas for display
          const enrichedInvalid = invalidBySupplier.map(f => {
            const info = result.missing.find(m => m.id === f.id);
            return Object.assign({}, f, { _supplierMissing: true, _supplierInfo: info });
          });
          setBulkValidationAlert({ open: true, invalidFacturas: enrichedInvalid as any, validFacturas: validBySupplier });
          return;
        }
      } catch (error) {
        console.error('Error al verificar proveedores:', error);
      } finally {
        setCheckingSupplier(false);
      }
    }
    setBulkConfirmDialog({ open: true, action });
  };

  const closeBulkConfirmDialog = () => {
    setBulkConfirmDialog({ open: false, action: null });
  };

  const handleBulkAction = async () => {
    if (!bulkConfirmDialog.action || selectedIds.size === 0) return;

    setIsProcessing(true);
    try {
      const ids = Array.from(selectedIds);
      const action = bulkConfirmDialog.action;

      if (action === 'eliminar') {
        await api.bulkDelete(ids);
      } else {
        const estadoMap: Record<string, string> = {
          aprobar: 'aprobada',
          en_espera: 'en_espera',
          rechazar: 'rechazada',
        };
        await api.bulkUpdateEstado(ids, estadoMap[action]);
      }

      setSelectedIds(new Set());
      closeBulkConfirmDialog();
      onRefresh?.();
    } catch (error) {
      console.error('Error al procesar accion en lote:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Aprobar solo las facturas válidas (con tipo y IVA)
  const handleApproveValidOnly = async () => {
    if (bulkValidationAlert.validFacturas.length === 0) return;

    setIsProcessing(true);
    try {
      const ids = bulkValidationAlert.validFacturas.map((f) => f.id);
      await api.bulkUpdateEstado(ids, 'aprobada');

      setSelectedIds(new Set());
      setBulkValidationAlert({ open: false, invalidFacturas: [], validFacturas: [] });
      onRefresh?.();
    } catch (error) {
      console.error('Error al aprobar facturas:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-2 text-muted-foreground">Cargando facturas...</p>
      </div>
    );
  }

  if (facturas.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">No se encontraron facturas</p>
      </div>
    );
  }

  const config = confirmDialog.action ? actionConfig[confirmDialog.action] : null;
  const bulkConfig = bulkConfirmDialog.action ? bulkActionConfig[bulkConfirmDialog.action] : null;

  return (
    <>
      {/* Barra de acciones bulk */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">
              {selectedIds.size} factura{selectedIds.size > 1 ? 's' : ''} seleccionada{selectedIds.size > 1 ? 's' : ''}
            </span>
            <span className="text-sm text-muted-foreground">
              Total: {formatCurrency(selectedTotal)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => openBulkConfirmDialog('aprobar')}
              disabled={checkingSupplier}
              className="text-green-600 hover:text-green-700"
            >
              {checkingSupplier ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
              {checkingSupplier ? 'Verificando...' : 'Aprobar'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openBulkConfirmDialog('en_espera')}
              className="text-blue-600 hover:text-blue-700"
            >
              <Clock className="h-4 w-4 mr-1" />
              En Espera
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openBulkConfirmDialog('rechazar')}
              className="text-orange-600 hover:text-orange-700"
            >
              <XCircle className="h-4 w-4 mr-1" />
              Rechazar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openBulkConfirmDialog('eliminar')}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Eliminar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
            >
              <X className="h-4 w-4 mr-1" />
              Cancelar
            </Button>
          </div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]">
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={(e) => handleSelectAll(e.target.checked)}
                aria-label="Seleccionar todas"
              />
            </TableHead>
            <TableHead>Numero</TableHead>
            <TableHead>Doc.</TableHead>
            <TableHead>Proveedor</TableHead>
            <TableHead>Receptor</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>IVA</TableHead>
            <TableHead>TAX</TableHead>
            <TableHead>
              <button
                className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                onClick={() => {
                  if (!onSortChange) return;
                  if (sortBy === 'fecha_emision') {
                    onSortChange('fecha_emision', sortDir === 'asc' ? 'desc' : 'asc');
                  } else {
                    onSortChange('fecha_emision', 'asc');
                  }
                }}
              >
                Emision
                {sortBy === 'fecha_emision' ? (
                  sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                ) : (
                  <ArrowUpDown className="h-3 w-3 opacity-50" />
                )}
              </button>
            </TableHead>
            <TableHead className="text-right">Valor</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="w-[40px] text-center">ERP</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {facturas.map((factura) => (
            <TableRow key={factura.id} className={`hover:bg-muted/50 ${selectedIds.has(factura.id) ? 'bg-muted/30' : ''}`}>
              <TableCell>
                <Checkbox
                  checked={selectedIds.has(factura.id)}
                  onChange={(e) => handleSelectOne(factura.id, e.target.checked)}
                  aria-label={`Seleccionar factura ${factura.numero}`}
                />
              </TableCell>
              <TableCell>
                <span className="font-medium">{factura.numero}</span>
              </TableCell>
              <TableCell>
                {factura.tipo_documento ? (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTipoDocumentoLabel(factura.tipo_documento).color}`}>
                    {getTipoDocumentoLabel(factura.tipo_documento).label}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                <div>
                  <p className="text-sm font-medium">{factura.proveedor?.razon_social || 'Sin proveedor'}</p>
                  {factura.proveedor?.nit && (
                    <p className="text-xs text-muted-foreground">NIT: {factura.proveedor.nit}</p>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div>
                  <p className="text-sm">{factura.receptor_nombre || '-'}</p>
                  {factura.receptor_nit && (
                    <p className="text-xs text-muted-foreground">NIT: {factura.receptor_nit}</p>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Select
                  value={factura.tipo_factura || 'sin_asignar'}
                  onValueChange={async (value) => {
                    if (value === 'limpiar') {
                      try {
                        await api.updateTipoFactura(factura.id, '', '');
                        onRefresh?.();
                      } catch (error) {
                        console.error('Error al limpiar tipo:', error);
                      }
                      return;
                    }
                    if (value === 'sin_asignar') return;
                    openSubtipoModal(value as CategoriaFactura, factura);
                  }}
                >
                  <SelectTrigger className="w-[180px] h-8 text-xs">
                    <SelectValue>
                      {factura.tipo_factura ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${tipoFacturaConfig[factura.tipo_factura as TipoFactura]?.color}`}>
                          {tipoFacturaConfig[factura.tipo_factura as TipoFactura]?.icon}
                          {factura.subtipo_factura
                            ? `${tipoFacturaConfig[factura.tipo_factura as TipoFactura]?.label} - ${getSubtipoNombre(factura.tipo_factura, factura.subtipo_factura, codigosContables)}`
                            : tipoFacturaConfig[factura.tipo_factura as TipoFactura]?.label
                          }
                          {factura.lineas_subtipo && <span className="text-[10px] opacity-70">(x línea)</span>}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Sin asignar</span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin_asignar" disabled>
                      <span className="text-muted-foreground">Sin asignar</span>
                    </SelectItem>
                    <SelectItem value="gasto">
                      <span className="inline-flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-orange-600" />
                        Gasto
                      </span>
                    </SelectItem>
                    <SelectItem value="compra">
                      <span className="inline-flex items-center gap-2">
                        <ShoppingCart className="h-4 w-4 text-blue-600" />
                        Compra
                      </span>
                    </SelectItem>
                    <SelectItem value="nota_credito">
                      <span className="inline-flex items-center gap-2">
                        <FileText className="h-4 w-4 text-green-600" />
                        Nota Crédito
                      </span>
                    </SelectItem>
                    {factura.tipo_factura && (
                      <SelectItem value="limpiar">
                        <span className="inline-flex items-center gap-2 text-red-500">
                          <X className="h-4 w-4" />
                          Limpiar asignación
                        </span>
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <button
                  type="button"
                  className="w-[100px] h-8 text-xs cursor-pointer rounded-md border border-input bg-background px-2 hover:bg-accent hover:text-accent-foreground flex items-center justify-center"
                  onClick={() => openIvaModal(factura)}
                >
                  {factura.tipo_iva ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {getTipoIvaLabel(factura.tipo_iva)}
                      {factura.lineas_iva && <span className="text-purple-500 text-[10px]">(x linea)</span>}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Sin IVA</span>
                  )}
                </button>
              </TableCell>
              <TableCell>
                {factura.tax_percent != null && factura.tax_percent !== '' ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {factura.tax_percent}%
                  </span>
                ) : (
                  <span className="text-muted-foreground text-xs">-</span>
                )}
              </TableCell>
              <TableCell>{formatDate(factura.fecha_emision)}</TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(factura.valor_total)}
              </TableCell>
              <TableCell>
                <Badge variant={estadoVariants[factura.estado]}>
                  {estadoLabels[factura.estado]}
                </Badge>
              </TableCell>
              <TableCell className="text-center">
                <div className="relative group inline-flex">
                  <Flag className={`h-4 w-4 ${factura.erp ? 'text-green-500' : 'text-gray-300'}`} />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                    {factura.erp ? 'Sincronizada en ERP' : 'No sincronizada en ERP'}
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                      <span className="sr-only">Acciones</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/factura/${factura.id}`)}>
                      <Eye className="mr-2 h-4 w-4" />
                      Ver Detalle
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => openConfirmDialog('aprobar', factura)}
                      disabled={factura.estado === 'aprobada' || (factura.estado === 'aprobada' && !isAdmin)}
                    >
                      <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                      Aprobar
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => openConfirmDialog('en_espera', factura)}
                      disabled={factura.estado === 'en_espera' || (factura.estado === 'aprobada' && !isAdmin)}
                    >
                      <Clock className="mr-2 h-4 w-4 text-blue-600" />
                      En Espera
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => openConfirmDialog('rechazar', factura)}
                      disabled={factura.estado === 'rechazada' || (factura.estado === 'aprobada' && !isAdmin)}
                    >
                      <XCircle className="mr-2 h-4 w-4 text-red-600" />
                      Rechazar
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => openConfirmDialog('eliminar', factura)}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t">
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">
            {total !== undefined
              ? `${total} factura${total !== 1 ? 's' : ''}`
              : `Pagina ${page} de ${totalPages}`}
            {limit > 0 && totalPages > 1 && ` · Pagina ${page} de ${totalPages}`}
          </p>
        </div>
        {limit > 0 && totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
            >
              Siguiente
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && closeConfirmDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {config?.icon}
              {config?.title}
            </DialogTitle>
            <DialogDescription>
              {config?.description}
              {confirmDialog.factura && (
                <div className="mt-4 p-3 bg-muted rounded-md">
                  <p className="font-medium">Factura: {confirmDialog.factura.numero}</p>
                  <p className="text-sm">Proveedor: {confirmDialog.factura.proveedor?.razon_social || 'Sin proveedor'}</p>
                  <p className="text-sm">Valor: {formatCurrency(confirmDialog.factura.valor_total)}</p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeConfirmDialog} disabled={isProcessing}>
              Cancelar
            </Button>
            <Button
              variant={config?.variant}
              onClick={handleConfirmAction}
              disabled={isProcessing}
            >
              {isProcessing ? 'Procesando...' : config?.confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Subtipos */}
      <Dialog open={subtipoModal.open} onOpenChange={(open) => !open && closeSubtipoModal()}>
        <DialogContent className={subtipoModal.categoria === 'gasto' ? 'sm:max-w-[700px]' : 'sm:max-w-[500px]'}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {subtipoModal.categoria && categoriasConfig[subtipoModal.categoria]?.icon}
              {subtipoModal.categoria === 'gasto'
                ? 'Seleccionar código contable'
                : `Seleccionar tipo de ${subtipoModal.categoria && categoriasConfig[subtipoModal.categoria]?.label}`
              }
            </DialogTitle>
            <DialogDescription>
              {subtipoModal.factura && (
                <span className="text-sm">
                  Factura: <strong>{subtipoModal.factura.numero}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Modal para Gasto - con buscador y tabla de códigos contables */}
          {subtipoModal.categoria === 'gasto' ? (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por código o nombre..."
                  value={compraSearch}
                  onChange={(e) => setCompraSearch(e.target.value)}
                  className="pl-9"
                  autoFocus
                />
              </div>
              <div className="max-h-[400px] overflow-y-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px]">Código</TableHead>
                      <TableHead>Nombre</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCompraItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-muted-foreground py-8">
                          No se encontraron items
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCompraItems.map((item) => (
                        <TableRow
                          key={item.codigo}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleCompraSelect(item)}
                        >
                          <TableCell className="font-mono text-sm font-medium text-blue-600">
                            {item.codigo}
                          </TableCell>
                          <TableCell className="text-sm">
                            {item.nombre}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm text-muted-foreground">
                  {filteredCompraItems.length} de {codigosContablesActivos.length} items
                </span>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <Button
                      variant="outline"
                      onClick={() => { closeSubtipoModal(); navigate('/admin/codigos-contables'); }}
                      disabled={isProcessing}
                    >
                      + Nuevo código
                    </Button>
                  )}
                  <Button variant="ghost" onClick={closeSubtipoModal} disabled={isProcessing}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </>
          ) : (
            /* Modal para otros tipos - con botones */
            <>
              <div className="grid gap-3 py-4 max-h-[400px] overflow-y-auto">
                {subtipoModal.categoria && categoriasConfig[subtipoModal.categoria]?.subtipos.map((subtipo) => (
                  <Button
                    key={subtipo.id}
                    variant="outline"
                    className="h-auto p-4 justify-start hover:bg-muted/50"
                    onClick={() => handleSubtipoSelect(subtipo)}
                    disabled={isProcessing}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="mt-0.5">{subtipo.icon}</div>
                      <div className="text-left">
                        <p className="font-medium">{subtipo.nombre}</p>
                        <p className="text-sm text-muted-foreground">
                          {subtipo.descripcion}
                        </p>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <Badge className={subtipoModal.categoria ? categoriasConfig[subtipoModal.categoria]?.color : ''}>
                  {subtipoModal.categoria && categoriasConfig[subtipoModal.categoria]?.label}
                </Badge>
                <Button variant="ghost" onClick={closeSubtipoModal} disabled={isProcessing}>
                  Cancelar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de validación - Campos requeridos para aprobar */}
      <Dialog open={validationAlert.open} onOpenChange={(open) => !open && setValidationAlert({ open: false, factura: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              Campos requeridos para aprobar
            </DialogTitle>
            <DialogDescription>
              Para aprobar esta factura es necesario asignar los siguientes campos:
              {validationAlert.factura && (
                <div className="mt-4 space-y-2">
                  <div className="p-3 bg-muted rounded-md">
                    <p className="font-medium">Factura: {validationAlert.factura.numero}</p>
                  </div>
                  <div className="space-y-1">
                    <p className={`text-sm flex items-center gap-2 ${validationAlert.factura.tipo_factura ? 'text-green-600' : 'text-red-600'}`}>
                      {validationAlert.factura.tipo_factura ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                      Tipo de Factura: {validationAlert.factura.tipo_factura ? 'Asignado' : 'Sin asignar'}
                    </p>
                    <p className={`text-sm flex items-center gap-2 ${validationAlert.factura.tipo_iva ? 'text-green-600' : 'text-red-600'}`}>
                      {validationAlert.factura.tipo_iva ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                      Tipo de IVA: {validationAlert.factura.tipo_iva ? 'Asignado' : 'Sin asignar'}
                    </p>
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setValidationAlert({ open: false, factura: null })}>
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de registro de proveedor - cuando falta en catálogo */}
      <Dialog open={supplierRegisterDialog.open} onOpenChange={(open) => !open && setSupplierRegisterDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <UserPlus className="h-5 w-5" />
              Proveedor no encontrado en catálogo
            </DialogTitle>
            <DialogDescription>
              {supplierRegisterDialog.existingSupplier
                ? 'El proveedor existe en el catálogo pero no tiene Codigo Cuenta (CTOCTA) asignado. Complete los datos para continuar.'
                : 'Este proveedor no está registrado en el catálogo. Registre los datos contables para aprobar la factura.'}
            </DialogDescription>
          </DialogHeader>

          {supplierRegisterDialog.factura && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-md space-y-1">
                <p className="text-sm"><span className="font-medium">Factura:</span> {supplierRegisterDialog.factura.numero}</p>
                <p className="text-sm"><span className="font-medium">NIT:</span> {supplierRegisterDialog.proveedor_nit}</p>
                <p className="text-sm"><span className="font-medium">Proveedor:</span> {supplierRegisterDialog.proveedor_razon_social}</p>
              </div>

              <div className="grid gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="codigo_cta" className="text-sm font-medium">
                      Codigo Cuenta (CTOCTA) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="codigo_cta"
                      value={supplierRegisterDialog.formData.codigo_cta}
                      onChange={(e) => setSupplierRegisterDialog(prev => ({
                        ...prev,
                        formData: { ...prev.formData, codigo_cta: e.target.value }
                      }))}
                      placeholder="Ej: 236540"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cuenta_x_pagar" className="text-sm font-medium">Cuenta x Pagar</Label>
                    <Input
                      id="cuenta_x_pagar"
                      value={supplierRegisterDialog.formData.cuenta_x_pagar}
                      onChange={(e) => setSupplierRegisterDialog(prev => ({
                        ...prev,
                        formData: { ...prev.formData, cuenta_x_pagar: e.target.value }
                      }))}
                      placeholder="Ej: 220505"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="cod_retencion" className="text-sm font-medium">Cod Retencion</Label>
                    <Input
                      id="cod_retencion"
                      value={supplierRegisterDialog.formData.cod_retencion}
                      onChange={(e) => setSupplierRegisterDialog(prev => ({
                        ...prev,
                        formData: { ...prev.formData, cod_retencion: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rtefte" className="text-sm font-medium">RTEFTE (%)</Label>
                    <Input
                      id="rtefte"
                      type="number"
                      step="0.01"
                      value={supplierRegisterDialog.formData.rtefte}
                      onChange={(e) => setSupplierRegisterDialog(prev => ({
                        ...prev,
                        formData: { ...prev.formData, rtefte: e.target.value }
                      }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="cod_rete" className="text-sm font-medium">Cod Rete</Label>
                    <Input
                      id="cod_rete"
                      value={supplierRegisterDialog.formData.cod_rete}
                      onChange={(e) => setSupplierRegisterDialog(prev => ({
                        ...prev,
                        formData: { ...prev.formData, cod_rete: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="top_rete" className="text-sm font-medium">Top Rete</Label>
                    <Input
                      id="top_rete"
                      type="number"
                      value={supplierRegisterDialog.formData.top_rete}
                      onChange={(e) => setSupplierRegisterDialog(prev => ({
                        ...prev,
                        formData: { ...prev.formData, top_rete: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="po_rete" className="text-sm font-medium">PO Rete</Label>
                    <Input
                      id="po_rete"
                      type="number"
                      step="0.01"
                      value={supplierRegisterDialog.formData.po_rete}
                      onChange={(e) => setSupplierRegisterDialog(prev => ({
                        ...prev,
                        formData: { ...prev.formData, po_rete: e.target.value }
                      }))}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cod_cc" className="text-sm font-medium">Cod CC</Label>
                  <Input
                    id="cod_cc"
                    value={supplierRegisterDialog.formData.cod_cc}
                    onChange={(e) => setSupplierRegisterDialog(prev => ({
                      ...prev,
                      formData: { ...prev.formData, cod_cc: e.target.value }
                    }))}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setSupplierRegisterDialog(prev => ({ ...prev, open: false }))} disabled={supplierRegisterDialog.loading}>
              Cancelar
            </Button>
            <Button
              onClick={handleRegisterAndApprove}
              disabled={!supplierRegisterDialog.formData.codigo_cta || supplierRegisterDialog.loading}
            >
              {supplierRegisterDialog.loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Procesando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Registrar y Aprobar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de IVA por líneas */}
      <Dialog open={ivaModal.open} onOpenChange={(open) => !open && setIvaModal({ open: false, factura: null, lines: [], lineAssignments: {}, retencionAssignments: {}, retentionOptions: [], defaultCodRetencion: '', loading: false })}>
        <DialogContent className="sm:max-w-[950px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Asignar Tipo de IVA por Linea
            </DialogTitle>
            <DialogDescription>
              {ivaModal.factura && (
                <span className="text-sm">
                  Factura: <strong>{ivaModal.factura.numero}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {ivaModal.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Cargando lineas...</span>
            </div>
          ) : ivaModal.lines.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No se encontraron lineas en el XML de esta factura.
            </div>
          ) : (
            <>
              <div className="max-h-[400px] overflow-y-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">#</TableHead>
                      <TableHead>Descripcion</TableHead>
                      <TableHead className="text-right w-[100px]">Subtotal</TableHead>
                      <TableHead className="text-right w-[80px]">IVA</TableHead>
                      <TableHead className="text-center w-[60px]">% Det.</TableHead>
                      <TableHead className="w-[160px]">Tipo IVA</TableHead>
                      <TableHead className="w-[120px]">Cod Ret</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ivaModal.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell className="font-mono text-sm">{line.id}</TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate" title={line.description}>
                          {line.description}
                        </TableCell>
                        <TableCell className="text-right text-sm font-mono">
                          {formatCurrency(line.subtotal)}
                        </TableCell>
                        <TableCell className="text-right text-sm font-mono">
                          {line.tax > 0 ? formatCurrency(line.tax) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            line.taxPercent > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {line.taxPercent}%
                          </span>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={ivaModal.lineAssignments[line.id] || ''}
                            onValueChange={(value) => handleIvaLineChange(line.id, value)}
                          >
                            <SelectTrigger className="w-[150px] h-7 text-xs">
                              <SelectValue placeholder="Seleccionar..." />
                            </SelectTrigger>
                            <SelectContent>
                              {tiposIvaConfig.map((tipo) => (
                                <SelectItem key={tipo.value} value={tipo.value}>
                                  <span className="text-xs">{tipo.label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={ivaModal.retencionAssignments[line.id] || '__none__'}
                            onValueChange={(value) => handleRetencionLineChange(line.id, value)}
                          >
                            <SelectTrigger className="w-[110px] h-7 text-xs">
                              <SelectValue placeholder="Sin ret." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">
                                <span className="text-xs text-muted-foreground">Sin retencion</span>
                              </SelectItem>
                              {ivaModal.retentionOptions.map((opt) => (
                                <SelectItem key={opt.cod_retencion} value={opt.cod_retencion}>
                                  <span className="text-xs">{opt.cod_retencion}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between pt-2 border-t">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">IVA a todas:</span>
                    <Select onValueChange={handleIvaApplyAll}>
                      <SelectTrigger className="w-[150px] h-7 text-xs">
                        <SelectValue placeholder="Aplicar a todas..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tiposIvaConfig.map((tipo) => (
                          <SelectItem key={tipo.value} value={tipo.value}>
                            <span className="text-xs">{tipo.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      setIvaModal(prev => {
                        const cleared: Record<string, string> = {};
                        for (const line of prev.lines) cleared[line.id] = '__none__';
                        return { ...prev, retencionAssignments: cleared };
                      });
                    }}
                  >
                    Quitar ret. todas
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setIvaModal({ open: false, factura: null, lines: [], lineAssignments: {}, retencionAssignments: {}, retentionOptions: [], defaultCodRetencion: '', loading: false })}
                    disabled={isProcessing}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveIva}
                    disabled={isProcessing || Object.values(ivaModal.lineAssignments).some(v => !v)}
                  >
                    {isProcessing ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Gasto por líneas (subtipo per line) */}
      <Dialog open={gastoLineasModal.open} onOpenChange={(open) => !open && setGastoLineasModal({ open: false, factura: null, lines: [], lineAssignments: {}, loading: false, search: '' })}>
        <DialogContent className="sm:max-w-[900px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Asignar Código Contable por Línea
            </DialogTitle>
            <DialogDescription>
              {gastoLineasModal.factura && (
                <span className="text-sm">
                  Factura: <strong>{gastoLineasModal.factura.numero}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {gastoLineasModal.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Cargando líneas...</span>
            </div>
          ) : gastoLineasModal.lines.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No se encontraron líneas en el XML de esta factura.
            </div>
          ) : (
            <>
              <div className="max-h-[450px] overflow-y-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">#</TableHead>
                      <TableHead className="max-w-[180px]">Descripción</TableHead>
                      <TableHead className="text-right w-[90px]">Subtotal</TableHead>
                      <TableHead className="text-right w-[70px]">IVA</TableHead>
                      <TableHead className="w-[250px]">Código Contable</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gastoLineasModal.lines.map((line) => {
                      const assignedCode = gastoLineasModal.lineAssignments[line.id] || '';
                      const assignedItem = codigosContables.find(i => i.codigo === assignedCode);
                      return (
                        <TableRow key={line.id}>
                          <TableCell className="font-mono text-sm">{line.id}</TableCell>
                          <TableCell className="text-sm max-w-[180px] truncate" title={line.description}>
                            {line.description}
                          </TableCell>
                          <TableCell className="text-right text-sm font-mono">
                            {formatCurrency(line.subtotal)}
                          </TableCell>
                          <TableCell className="text-right text-sm font-mono">
                            {line.tax > 0 ? formatCurrency(line.tax) : '-'}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={assignedCode}
                              onValueChange={(value) => handleGastoLineChange(line.id, value)}
                            >
                              <SelectTrigger className="w-[230px] h-7 text-xs">
                                <SelectValue placeholder="Seleccionar...">
                                  {assignedItem ? (
                                    <span className="truncate">{assignedItem.nombre}</span>
                                  ) : (
                                    <span className="text-muted-foreground">Seleccionar...</span>
                                  )}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent className="max-h-[300px]">
                                <div className="p-2 sticky top-0 bg-white z-10">
                                  <Input
                                    placeholder="Buscar código..."
                                    value={gastoLineasModal.search}
                                    onChange={(e) => setGastoLineasModal(prev => ({ ...prev, search: e.target.value }))}
                                    className="h-7 text-xs"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                {filteredGastoLineasItems.map((item) => (
                                  <SelectItem key={item.codigo} value={item.codigo}>
                                    <span className="text-xs"><span className="font-medium text-muted-foreground">{item.codigo}</span> - {item.nombre}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between pt-2 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Asignar a todas:</span>
                  <Select onValueChange={handleGastoApplyAll}>
                    <SelectTrigger className="w-[230px] h-7 text-xs">
                      <SelectValue placeholder="Aplicar a todas..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <div className="p-2 sticky top-0 bg-white z-10">
                        <Input
                          placeholder="Buscar código..."
                          value={gastoLineasModal.search}
                          onChange={(e) => setGastoLineasModal(prev => ({ ...prev, search: e.target.value }))}
                          className="h-7 text-xs"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {filteredGastoLineasItems.map((item) => (
                        <SelectItem key={item.codigo} value={item.codigo}>
                          <span className="text-xs"><span className="font-medium text-muted-foreground">{item.codigo}</span> - {item.nombre}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setGastoLineasModal({ open: false, factura: null, lines: [], lineAssignments: {}, loading: false, search: '' })}
                    disabled={isProcessing}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveGastoLineas}
                    disabled={isProcessing || Object.values(gastoLineasModal.lineAssignments).some(v => !v)}
                  >
                    {isProcessing ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación de acciones bulk */}
      <Dialog open={bulkConfirmDialog.open} onOpenChange={(open) => !open && closeBulkConfirmDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkConfig?.icon}
              {bulkConfig?.title}
            </DialogTitle>
            <DialogDescription>
              {bulkConfig?.description(selectedIds.size)}
              <div className="mt-4 p-3 bg-muted rounded-md">
                <p className="font-medium">{selectedIds.size} factura{selectedIds.size > 1 ? 's' : ''}</p>
                <p className="text-sm">Valor total: {formatCurrency(selectedTotal)}</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeBulkConfirmDialog} disabled={isProcessing}>
              Cancelar
            </Button>
            <Button
              variant={bulkConfig?.variant}
              onClick={handleBulkAction}
              disabled={isProcessing}
            >
              {isProcessing ? 'Procesando...' : bulkConfig?.confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de validación bulk para aprobar */}
      <Dialog open={bulkValidationAlert.open} onOpenChange={(open) => !open && setBulkValidationAlert({ open: false, invalidFacturas: [], validFacturas: [] })}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              Facturas con campos faltantes
            </DialogTitle>
            <DialogDescription>
              Algunas facturas no pueden ser aprobadas por datos faltantes:
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[200px] overflow-y-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numero</TableHead>
                  <TableHead>Problema</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bulkValidationAlert.invalidFacturas.map((factura: any) => (
                  <TableRow key={factura.id}>
                    <TableCell className="font-medium">{factura.numero}</TableCell>
                    <TableCell className="text-sm text-red-600">
                      {!factura.tipo_factura && <span className="flex items-center gap-1"><XCircle className="h-3 w-3" /> Sin Tipo</span>}
                      {!factura.tipo_iva && <span className="flex items-center gap-1"><XCircle className="h-3 w-3" /> Sin IVA</span>}
                      {factura._supplierMissing && <span className="flex items-center gap-1"><XCircle className="h-3 w-3" /> Proveedor sin CTOCTA</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="text-sm text-muted-foreground">
            {bulkValidationAlert.validFacturas.length > 0 ? (
              <p>
                {bulkValidationAlert.validFacturas.length} factura{bulkValidationAlert.validFacturas.length > 1 ? 's' : ''} puede{bulkValidationAlert.validFacturas.length > 1 ? 'n' : ''} ser aprobada{bulkValidationAlert.validFacturas.length > 1 ? 's' : ''}.
              </p>
            ) : (
              <p>Ninguna factura seleccionada puede ser aprobada.</p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkValidationAlert({ open: false, invalidFacturas: [], validFacturas: [] })}
              disabled={isProcessing}
            >
              Cancelar
            </Button>
            {bulkValidationAlert.validFacturas.length > 0 && (
              <Button
                onClick={handleApproveValidOnly}
                disabled={isProcessing}
              >
                {isProcessing ? 'Procesando...' : `Aprobar ${bulkValidationAlert.validFacturas.length} valida${bulkValidationAlert.validFacturas.length > 1 ? 's' : ''}`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Compra - Items del XML con equivalencias */}
      <Dialog open={compraModal.open} onOpenChange={(open) => !open && setCompraModal({ open: false, factura: null, items: [], loading: false, lookingUp: false, proveedorNit: '', newEquivalencias: {} })}>
        <DialogContent className="sm:max-w-[950px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />
              Confirmar items de Compra
            </DialogTitle>
            <DialogDescription>
              {compraModal.factura && (
                <span className="text-sm">
                  Factura: <strong>{compraModal.factura.numero}</strong> — Asigne codigos internos a cada producto y confirme.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {compraModal.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-3 text-muted-foreground">Cargando items...</span>
            </div>
          ) : compraModal.items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No se encontraron items en el XML de esta factura.
            </div>
          ) : (
            <>
              {compraModal.lookingUp && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-md text-sm text-blue-700">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Buscando codigos internos en equivalencias...
                </div>
              )}
              <div className="max-h-[400px] overflow-y-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">#</TableHead>
                      <TableHead>Descripcion</TableHead>
                      <TableHead className="text-right w-[60px]">Cant.</TableHead>
                      <TableHead className="text-right w-[90px]">Precio</TableHead>
                      <TableHead className="text-right w-[90px]">Subtotal</TableHead>
                      <TableHead className="w-[180px]">Cod. Interno</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {compraModal.items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-muted-foreground">{item.id || idx + 1}</TableCell>
                        <TableCell className="text-sm">
                          {item.description}
                          {item.externalCode !== item.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 font-mono">SKU: {item.externalCode}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.price)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(item.subtotal)}</TableCell>
                        <TableCell>
                          {item.internalCode ? (
                            <div className="flex items-center gap-1.5">
                              <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                              <span className="text-sm font-mono font-medium text-green-700">{item.internalCode}</span>
                            </div>
                          ) : compraModal.lookingUp ? (
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              <span className="text-xs">Buscando...</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                              <Input
                                className="h-7 text-xs font-mono w-full"
                                placeholder="Cod. interno"
                                value={compraModal.newEquivalencias[item.externalCode] || ''}
                                onChange={(e) => handleInternalCodeChange(item.externalCode, e.target.value)}
                              />
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          <div className="flex justify-between items-center pt-2 border-t">
            <span className="text-sm text-muted-foreground">
              {compraModal.items.length} item{compraModal.items.length !== 1 ? 's' : ''}
              {compraModal.items.length > 0 && (() => {
                const resolvedCount = compraModal.items.filter((i) =>
                  i.internalCode || (compraModal.newEquivalencias[i.externalCode] || '').trim()
                ).length;
                const pending = compraModal.items.length - resolvedCount;
                return (
                  <>
                    {' '}&mdash; {resolvedCount}/{compraModal.items.length} con codigo
                    {pending > 0 && (
                      <span className="text-amber-600 ml-1">
                        ({pending} pendiente{pending > 1 ? 's' : ''})
                      </span>
                    )}
                  </>
                );
              })()}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setCompraModal({ open: false, factura: null, items: [], loading: false, lookingUp: false, proveedorNit: '', newEquivalencias: {} })} disabled={isProcessing}>
                Cancelar
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href="https://betsupplier-equivalencias.carlos-mdt-1057.workers.dev/" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-1.5" /> Portal Equivalencias
                </a>
              </Button>
              <Button onClick={handleConfirmCompra} disabled={isProcessing || compraModal.items.length === 0 || compraModal.lookingUp || !allItemsHaveInternalCode}>
                {isProcessing ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Guardando...</>
                ) : (
                  <><ShoppingCart className="h-4 w-4 mr-2" /> Confirmar Compra</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Nota Crédito - 2 categorías */}
      <Dialog open={notaCreditoModal.open} onOpenChange={(open) => !open && closeNotaCreditoModal()}>
        <DialogContent className={notaCreditoModal.step === 'select' ? 'sm:max-w-[500px]' : 'sm:max-w-[700px]'}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              {notaCreditoModal.step === 'select' && 'Nota de Crédito'}
              {notaCreditoModal.step === 'gastos' && 'Nota Crédito — Gastos'}
              {notaCreditoModal.step === 'mercancia' && 'Nota Crédito — Mercancía'}
            </DialogTitle>
            <DialogDescription>
              {notaCreditoModal.factura && (
                <span className="text-sm">
                  Factura: <strong>{notaCreditoModal.factura.numero}</strong>
                  {notaCreditoModal.step === 'select' && ' — Seleccione el tipo de nota crédito.'}
                  {notaCreditoModal.step === 'gastos' && ' — Seleccione el código contable.'}
                  {notaCreditoModal.step === 'mercancia' && ' — Revise los items y confirme.'}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Paso 1: Seleccionar categoría */}
          {notaCreditoModal.step === 'select' && (
            <div className="grid gap-3 py-2">
              <Button
                variant="outline"
                className="h-auto p-4 justify-start hover:bg-muted/50"
                onClick={() => setNotaCreditoModal((prev) => ({ ...prev, step: 'gastos' }))}
              >
                <div className="flex items-center gap-3 w-full">
                  <Receipt className="h-5 w-5 text-orange-600" />
                  <div className="text-left">
                    <p className="font-medium">Nota Crédito de Gastos</p>
                    <p className="text-sm text-muted-foreground">Seleccionar código contable del listado</p>
                  </div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="h-auto p-4 justify-start hover:bg-muted/50"
                onClick={() => setNotaCreditoModal((prev) => ({ ...prev, step: 'mercancia' }))}
              >
                <div className="flex items-center gap-3 w-full">
                  <Package className="h-5 w-5 text-blue-600" />
                  <div className="text-left">
                    <p className="font-medium">Nota Crédito de Mercancía</p>
                    <p className="text-sm text-muted-foreground">Ver y confirmar los productos de la factura</p>
                  </div>
                </div>
              </Button>
            </div>
          )}

          {/* Paso 2a: Gastos - listado de códigos contables */}
          {notaCreditoModal.step === 'gastos' && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por código o nombre..."
                  value={ncGastoSearch}
                  onChange={(e) => setNcGastoSearch(e.target.value)}
                  className="pl-9"
                  autoFocus
                />
              </div>
              <div className="max-h-[400px] overflow-y-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px]">Código</TableHead>
                      <TableHead>Nombre</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredNcGastoItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-muted-foreground py-8">
                          No se encontraron items
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredNcGastoItems.map((item) => (
                        <TableRow
                          key={item.codigo}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleNcGastoSelect(item)}
                        >
                          <TableCell className="font-mono text-sm font-medium text-green-600">
                            {item.codigo}
                          </TableCell>
                          <TableCell className="text-sm">{item.nombre}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm text-muted-foreground">
                  {filteredNcGastoItems.length} de {codigosContablesActivos.length} items
                </span>
                <Button variant="ghost" onClick={() => setNotaCreditoModal((prev) => ({ ...prev, step: 'select' }))} disabled={isProcessing}>
                  Volver
                </Button>
              </div>
            </>
          )}

          {/* Paso 2b: Mercancía - items del XML */}
          {notaCreditoModal.step === 'mercancia' && (
            <>
              {notaCreditoModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-green-600" />
                  <span className="ml-3 text-muted-foreground">Cargando items...</span>
                </div>
              ) : notaCreditoModal.items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No se encontraron items en el XML de esta factura.
                </div>
              ) : (
                <div className="max-h-[400px] overflow-y-auto border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">#</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead className="text-right w-[70px]">Cant.</TableHead>
                        <TableHead className="text-right w-[100px]">Precio</TableHead>
                        <TableHead className="text-right w-[100px]">Subtotal</TableHead>
                        <TableHead className="text-right w-[80px]">IVA</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {notaCreditoModal.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-muted-foreground">{item.id || idx + 1}</TableCell>
                          <TableCell className="text-sm">{item.description}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.price)}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(item.subtotal)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.tax)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm text-muted-foreground">
                  {notaCreditoModal.items.length} item{notaCreditoModal.items.length !== 1 ? 's' : ''}
                  {notaCreditoModal.items.length > 0 && (
                    <> — Total: <strong>{formatCurrency(notaCreditoModal.items.reduce((s, i) => s + i.subtotal + i.tax, 0))}</strong></>
                  )}
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setNotaCreditoModal((prev) => ({ ...prev, step: 'select' }))} disabled={isProcessing}>
                    Volver
                  </Button>
                  <Button onClick={handleConfirmNcMercancia} disabled={isProcessing || notaCreditoModal.items.length === 0}>
                    {isProcessing ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Guardando...</>
                    ) : (
                      <><Package className="h-4 w-4 mr-2" /> Confirmar Mercancía</>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Botón cancelar solo en paso 1 */}
          {notaCreditoModal.step === 'select' && (
            <div className="flex justify-end pt-2 border-t">
              <Button variant="ghost" onClick={closeNotaCreditoModal} disabled={isProcessing}>
                Cancelar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
